import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const EXCLUDED_PATH_PREFIXES = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/invite',
  '/change-password',
  '/enroll-mfa',
  '/profile',
  '/jsa-guest-sign',
  '/platform-admin/security',
];

const ERROR_ATTRIBUTE = 'data-required-field-error';
const MANAGED_ATTRIBUTE = 'data-required-field-managed';
const INFERRED_ATTRIBUTE = 'data-required-field-inferred';
const INFERRED_MESSAGE_ATTRIBUTE = 'data-required-field-inferred-message';
const VALIDATION_BOUNDARY = '[data-required-validation-boundary="true"]';
const VALIDATION_ACTION =
  /^(assign|attach|calculate|check|complete|convert|create|generate|instantiate|mark|post|record|resolve|save|schedule|submit|update)\b/i;
const managedFieldState = new WeakMap<
  HTMLElement,
  {
    previousAriaInvalid: string | null;
    changedAriaInvalid: boolean;
    addedRing: boolean;
    addedRedRing: boolean;
    addedRedBorder: boolean;
  }
>();

function isExcludedPath(pathname: string) {
  return EXCLUDED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function isEmptyRequiredField(element: HTMLElement) {
  if (element.dataset.required === 'true') {
    return element.dataset.requiredEmpty === 'true';
  }

  if (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement
  ) {
    const inferred = element.getAttribute(INFERRED_ATTRIBUTE) === 'true';
    if (element.disabled || (!element.required && !inferred)) return false;
    if (element instanceof HTMLInputElement) {
      if (element.type === 'checkbox') return !element.checked;
      if (element.type === 'radio') return element.validity.valueMissing;
      return element.value.trim() === '';
    }
    return element.value.trim() === '';
  }

  if (element.getAttribute(INFERRED_ATTRIBUTE) === 'true') {
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      if (
        element instanceof HTMLInputElement &&
        (element.type === 'checkbox' || element.type === 'radio')
      ) {
        return !element.checked;
      }
      return element.value.trim() === '';
    }
    if (element instanceof HTMLSelectElement) return element.value === '';
    return element.hasAttribute('data-placeholder') || element.textContent?.trim() === '';
  }

  return false;
}

function fieldMessage(element: HTMLElement) {
  if (element.dataset.requiredMessage) return element.dataset.requiredMessage;

  if (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement
  ) {
    return element.validity.valueMissing || !element.validationMessage
      ? 'This field is required.'
      : element.validationMessage;
  }

  return 'This field is required.';
}

function hasDisablingFieldsetAncestor(element: HTMLElement) {
  let ancestor = element.parentElement;
  while (ancestor) {
    if (ancestor instanceof HTMLFieldSetElement && ancestor.disabled) {
      const firstLegend = Array.from(ancestor.children).find(
        (child) => child instanceof HTMLLegendElement,
      );
      if (!firstLegend?.contains(element)) return true;
    }
    ancestor = ancestor.parentElement;
  }
  return false;
}

function isEffectivelyDisabled(element: HTMLElement) {
  if (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement ||
    element instanceof HTMLButtonElement
  ) {
    return element.matches(':disabled');
  }

  return (
    element.hasAttribute('disabled') ||
    element.getAttribute('aria-disabled') === 'true' ||
    hasDisablingFieldsetAncestor(element)
  );
}

function clearFieldError(element: HTMLElement) {
  if (element.getAttribute(MANAGED_ATTRIBUTE) !== 'true') return;

  const errorId = element.dataset.requiredErrorId;
  if (errorId) {
    document.getElementById(errorId)?.remove();
    const describedBy = (element.getAttribute('aria-describedby') ?? '')
      .split(/\s+/)
      .filter((id) => id && id !== errorId)
      .join(' ');
    if (describedBy) {
      element.setAttribute('aria-describedby', describedBy);
    } else {
      element.removeAttribute('aria-describedby');
    }
  }

  const managed = managedFieldState.get(element);
  if (managed?.changedAriaInvalid && element.getAttribute('aria-invalid') === 'true') {
    if (managed.previousAriaInvalid === null) {
      element.removeAttribute('aria-invalid');
    } else {
      element.setAttribute('aria-invalid', managed.previousAriaInvalid);
    }
  }
  if (managed?.addedRing) element.classList.remove('ring-1');
  if (managed?.addedRedRing) element.classList.remove('ring-red-500');
  if (managed?.addedRedBorder) element.classList.remove('border-red-500');
  managedFieldState.delete(element);
  element.removeAttribute(MANAGED_ATTRIBUTE);
  delete element.dataset.requiredErrorId;
}

function clearManagedErrors(scope: ParentNode) {
  if (scope instanceof HTMLElement && scope.getAttribute(MANAGED_ATTRIBUTE) === 'true') {
    clearFieldError(scope);
  }
  scope
    .querySelectorAll<HTMLElement>(`[${MANAGED_ATTRIBUTE}="true"]`)
    .forEach(clearFieldError);
}

function renderFieldError(element: HTMLElement, index: number) {
  clearFieldError(element);
  const previousAriaInvalid = element.getAttribute('aria-invalid');
  managedFieldState.set(element, {
    previousAriaInvalid,
    changedAriaInvalid: previousAriaInvalid !== 'true',
    addedRing: !element.classList.contains('ring-1'),
    addedRedRing: !element.classList.contains('ring-red-500'),
    addedRedBorder: !element.classList.contains('border-red-500'),
  });

  const existingId = element.id || `required-field-${index}`;
  if (!element.id) element.id = existingId;
  const errorId = `${existingId}-required-error`;
  const error = document.createElement('p');
  error.id = errorId;
  error.setAttribute(ERROR_ATTRIBUTE, 'true');
  error.setAttribute('role', 'alert');
  error.className = 'mt-1 text-sm text-red-600';
  error.textContent = fieldMessage(element);

  const container = element.closest<HTMLElement>('[data-field-container]') ?? element.parentElement;
  container?.appendChild(error);

  const describedBy = new Set(
    (element.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(Boolean),
  );
  describedBy.add(errorId);
  element.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
  element.setAttribute('aria-invalid', 'true');
  element.classList.add('ring-1', 'ring-red-500', 'border-red-500');
  element.setAttribute(MANAGED_ATTRIBUTE, 'true');
  element.dataset.requiredErrorId = errorId;
}

function inferRequiredFields(scope: ParentNode) {
  scope
    .querySelectorAll<HTMLElement>(`[${INFERRED_ATTRIBUTE}="true"]`)
    .forEach((element) => {
      clearFieldError(element);
      element.removeAttribute(INFERRED_ATTRIBUTE);
      if (element.getAttribute(INFERRED_MESSAGE_ATTRIBUTE) === 'true') {
        delete element.dataset.requiredMessage;
        element.removeAttribute(INFERRED_MESSAGE_ATTRIBUTE);
      }
    });

  scope.querySelectorAll<HTMLLabelElement>('label').forEach((label) => {
    const labelText = label.textContent?.trim() ?? '';
    if (!labelText.includes('*')) return;

    const labelledControl = label.htmlFor ? document.getElementById(label.htmlFor) : null;
    const container = label.closest<HTMLElement>('[data-field-container]') ?? label.parentElement;
    if (!labelledControl && container?.querySelector('[data-required="true"]')) return;
    const element =
      labelledControl ??
      container?.querySelector<HTMLElement>(
        'input:not([type="hidden"]), textarea, select, [role="combobox"]',
      );
    if (!element || element.hasAttribute('required') || element.dataset.required === 'true') return;

    element.setAttribute(INFERRED_ATTRIBUTE, 'true');
    if (!element.dataset.requiredMessage) {
      const fieldName = labelText.replace(/\s*\*\s*/g, '').trim();
      element.dataset.requiredMessage = `${fieldName || 'This field'} is required.`;
      element.setAttribute(INFERRED_MESSAGE_ATTRIBUTE, 'true');
    }
  });
}

function validationFields(scope: ParentNode) {
  inferRequiredFields(scope);
  return Array.from(
    scope.querySelectorAll<HTMLElement>(
      `input[required], textarea[required], select[required], [data-required="true"], [${INFERRED_ATTRIBUTE}="true"]`,
    ),
  ).filter(
    (element) => {
      const boundary = element.closest<HTMLElement>(VALIDATION_BOUNDARY);
      const scopeIsWithinBoundary =
        boundary !== null && scope instanceof Node && boundary.contains(scope);
      return (
        element.getAttribute('type') !== 'hidden' &&
        !isEffectivelyDisabled(element) &&
        !element.closest('[hidden], [aria-hidden="true"], [data-state="inactive"]') &&
        (!boundary || boundary === scope || scopeIsWithinBoundary)
      );
    },
  );
}

function validateRequiredFields(scope: ParentNode) {
  const fields = validationFields(scope);
  fields.forEach(clearFieldError);

  const invalidFields = fields.filter(isEmptyRequiredField);
  invalidFields.forEach(renderFieldError);

  const firstInvalid = invalidFields[0];
  if (firstInvalid) {
    firstInvalid.focus({ preventScroll: true });
    firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  return invalidFields.length === 0;
}

function resolveScope(control: HTMLElement) {
  const scopeId = control.dataset.validationScope;
  if (scopeId) return document.getElementById(scopeId);

  if (
    (control instanceof HTMLButtonElement && control.type === 'submit') ||
    (control instanceof HTMLInputElement && control.type === 'submit')
  ) {
    const form = control.form;
    if (form) return form;
  }

  return (
    control.closest('form') ??
    control.closest('[role="dialog"]') ??
    control.closest('[data-slot="card"]') ??
    control.closest('main') ??
    document
  );
}

export function RequiredFieldValidation() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (isExcludedPath(pathname)) return;

    const validateAction = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const submittedForm = event.type === 'submit' && target instanceof HTMLFormElement ? target : null;
      const control = submittedForm ? null : target.closest<HTMLElement>('button, input[type="submit"]');
      if (!submittedForm && (!control || control.getAttribute('disabled') !== null)) return;
      if (control?.dataset.skipRequiredValidation === 'true') return;
      if (
        control &&
        control.getAttribute('type') !== 'submit' &&
        control.dataset.validateRequired !== 'true' &&
        !VALIDATION_ACTION.test(control.textContent?.trim() ?? '')
      ) {
        return;
      }

      const scope = submittedForm ?? (control ? resolveScope(control) : null);
      if (!scope || validateRequiredFields(scope)) return;

      event.preventDefault();
      event.stopPropagation();
    };

    const handleInvalid = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const scope = target.closest('form');
      if (!scope || !isEmptyRequiredField(target)) return;

      event.preventDefault();
      window.requestAnimationFrame(() => validateRequiredFields(scope));
    };

    const clearResolvedError = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      window.requestAnimationFrame(() => {
        const field =
          target.closest<HTMLElement>(`[${MANAGED_ATTRIBUTE}="true"]`) ?? target;
        if (!isEmptyRequiredField(field)) clearFieldError(field);
      });
    };

    const requiredStateObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const target = mutation.target;
        if (!(target instanceof HTMLElement)) return;
        if (mutation.attributeName === 'disabled' || mutation.attributeName === 'aria-disabled') {
          const managedFields = Array.from(
            target.querySelectorAll<HTMLElement>(`[${MANAGED_ATTRIBUTE}="true"]`),
          );
          if (target.getAttribute(MANAGED_ATTRIBUTE) === 'true') {
            managedFields.unshift(target);
          }
          managedFields.forEach((field) => {
            if (isEffectivelyDisabled(field)) clearFieldError(field);
          });
          return;
        }
        const field =
          target.closest<HTMLElement>(`[${MANAGED_ATTRIBUTE}="true"]`) ?? target;
        if (
          field.getAttribute(MANAGED_ATTRIBUTE) === 'true' &&
          !isEmptyRequiredField(field)
        ) {
          clearFieldError(field);
        }
      });
    });
    requiredStateObserver.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: [
        'aria-disabled',
        'data-placeholder',
        'data-required-empty',
        'disabled',
      ],
    });

    document.addEventListener('click', validateAction, true);
    document.addEventListener('submit', validateAction, true);
    document.addEventListener('invalid', handleInvalid, true);
    document.addEventListener('input', clearResolvedError, true);
    document.addEventListener('change', clearResolvedError, true);
    document.addEventListener('click', clearResolvedError, true);

    return () => {
      requiredStateObserver.disconnect();
      clearManagedErrors(document);
      document
        .querySelectorAll<HTMLElement>(`[${INFERRED_ATTRIBUTE}="true"]`)
        .forEach((element) => {
          element.removeAttribute(INFERRED_ATTRIBUTE);
          if (element.getAttribute(INFERRED_MESSAGE_ATTRIBUTE) === 'true') {
            delete element.dataset.requiredMessage;
            element.removeAttribute(INFERRED_MESSAGE_ATTRIBUTE);
          }
        });
      document.removeEventListener('click', validateAction, true);
      document.removeEventListener('submit', validateAction, true);
      document.removeEventListener('invalid', handleInvalid, true);
      document.removeEventListener('input', clearResolvedError, true);
      document.removeEventListener('change', clearResolvedError, true);
      document.removeEventListener('click', clearResolvedError, true);
    };
  }, [pathname]);

  return null;
}
